import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {WeeklyHowItsGoingV2} from '@shared/api/models/query/patient-weekly-snapshot.model';
import {WeeklyHowItsGoingAnswerLabels} from '@cdss/pages/patient-overview-v2/detail/weekly/weekly-carer-copy';
import {WeeklyStateLabelComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-state-label/weekly-state-label.component';

/**
 * Carer weekly summary — "How it's going" panel (UNIVAPLAT-3038, ticket description).
 * Unlike its sibling v2 panels (meal plan, behaviours, daily check-ins roll-up),
 * which stay "dumb" and let the page own the section title, this component owns
 * its own title per the ticket's explicit file-level description ("icon + title
 * 'How it's going'"). No icon design/asset was supplied with the ticket — none is
 * rendered here; flagged as an assumption in the sub-task's DEV-DONE comment,
 * same handling as the meal plan panel's title/icon ambiguity in UNIVAPLAT-3037.
 *
 * Renders the two `howItsGoing` answers as simple labeled values — no graphs, no
 * history, unlike the "Your observations" panel alongside it:
 *
 *  - "Carer wellbeing" — `progressFeeling` ('notGood'/'okay'/'pleased').
 *  - "Univa helpful" — `univaHelpfulness` ('notVery'/'aLittle'/'very').
 *
 * Two distinct empty states per the ticket text (not the same blank path):
 *  - `howItsGoing` null, or both answers null — the whole panel shows the shared
 *    `noCheckIn` state with the ticket's exact copy, "Not answered this week".
 *  - A single answer null (the other answered) — only that line shows the shared
 *    `notRated` state ("Not rated").
 */
@Component({
    selector: 'cdss-weekly-how-its-going',
    standalone: true,
    imports: [CommonModule, WeeklyStateLabelComponent],
    templateUrl: './weekly-how-its-going.component.html',
    styleUrl: './weekly-how-its-going.component.scss'
})
export class WeeklyHowItsGoingComponent {

    @Input() howItsGoing: WeeklyHowItsGoingV2 | null = null;
    @Input() answerLabels!: WeeklyHowItsGoingAnswerLabels;

    get isFullyUnanswered(): boolean {
        return !this.howItsGoing
            || (this.howItsGoing.progressFeeling === null && this.howItsGoing.univaHelpfulness === null);
    }

    get progressFeelingLabel(): string | null {
        const value = this.howItsGoing?.progressFeeling;
        return value ? this.answerLabels.progressFeeling[value] : null;
    }

    get univaHelpfulnessLabel(): string | null {
        const value = this.howItsGoing?.univaHelpfulness;
        return value ? this.answerLabels.univaHelpfulness[value] : null;
    }
}
